import React from 'react';
import { Modal, Button } from 'antd';
import { Star, Trophy, X } from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';

const BadgeInfoModal = ({ isOpen, onClose, currentBadge = 'Growing Employee', nextBadge = 'Best Employee', badgeRanges = [], avgScore = null }) => {
  const { mode } = useTheme();
  const isDark = mode === 'dark';

  // Default badge descriptions
  const defaultBadgeInfo = {
    'Growing Employee': {
      description: "Awarded for consistent goal completion and strong performance in your early cycles. You've shown reliability and growth."
    },
    'Best Employee': {
      description: "Achieved by consistently meeting your goals and demonstrating strong performance. This badge reflects your progress from Beginner to Rising Star level through dedicated effort."
    },
    'Need to Improve': {
      description: "This level indicates there's room for improvement in goal completion and performance. Focus on setting achievable goals and working consistently towards them."
    }
  };

  // Find current badge info from badgeRanges or use default
  const currentBadgeRange = badgeRanges.find(range =>
    range.label.toLowerCase() === currentBadge.toLowerCase() ||
    currentBadge.toLowerCase().includes(range.label.toLowerCase()) ||
    range.label.toLowerCase().includes(currentBadge.toLowerCase())
  );

  // Find next badge info from badgeRanges
  const nextBadgeRange = badgeRanges.find(range =>
    range.label.toLowerCase() === nextBadge.toLowerCase() ||
    nextBadge.toLowerCase().includes(range.label.toLowerCase()) ||
    range.label.toLowerCase().includes(nextBadge.toLowerCase())
  );

  // Check if current badge is the last/highest badge
  const isLastBadge = () => {
    if (!badgeRanges.length || !currentBadgeRange) return false;
    
    // Sort badges by max value (excluding lessThan badges for comparison)
    const sortedBadges = [...badgeRanges]
      .filter(range => !range.lessThan)
      .sort((a, b) => b.max - a.max);
    
    // Check if current badge has the highest max value
    if (sortedBadges.length > 0 && !currentBadgeRange.lessThan) {
      const highestBadge = sortedBadges[0];
      return currentBadgeRange.max === highestBadge.max && 
             currentBadgeRange.min === highestBadge.min;
    }
    
    return false;
  };

  // Get current badge description
  const getCurrentDescription = () => {
    if (currentBadgeRange) {
      if (currentBadgeRange.lessThan) {
        return `Your average score is below ${currentBadgeRange.max}. Focus on improving your goal completion and performance to move to the next level.`;
      } else {
        // Check if this is the last badge
        if (isLastBadge()) {
          return `Your average score is between ${currentBadgeRange.min} and ${currentBadgeRange.max}. Keep up the good work and continue maintaining your score`;
        } else {
          return `Your average score is between ${currentBadgeRange.min} and ${currentBadgeRange.max}. Keep up the good work and aim for the next badge!`;
        }
      }
    }
    return defaultBadgeInfo[currentBadge]?.description || defaultBadgeInfo['Growing Employee'].description;
  };

  // Get next badge criteria
  const getNextCriteria = () => {
    if (nextBadgeRange) {
      if (nextBadgeRange.lessThan) {
        return `If your avg score is less than ${nextBadgeRange.max}`;
      } else {
        return `If your avg score is between ${nextBadgeRange.min} to ${nextBadgeRange.max}`;
      }
    }
    // Fallback to default
    if (nextBadge === 'Best Employee') {
      return "If your avg score is between 8.5 to 10";
    }
    return "Keep improving your performance to unlock this badge";
  };

  const currentDescription = getCurrentDescription();
  const nextCriteria = getNextCriteria();

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      footer={null}
      closable={false}
      width={480}
      centered
      className="badge-info-modal"
      styles={{
        content: {
          padding: 0,
          borderRadius: '16px',
          fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
        }
      }}
    >
      <div className={`${isDark ? 'bg-white/5' : 'bg-white'} rounded-2xl overflow-hidden`} style={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif' }}>
        {/* Header */}
        <div className={`px-6 py-5 border-b ${isDark ? 'border-white/10' : 'border-slate-100'} flex items-start justify-between`}>
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center overflow-hidden ${isDark ? 'bg-yellow-500/10 border border-yellow-500/20' : 'bg-yellow-50 border border-yellow-100'}`}>
              {currentBadgeRange?.icon?.url ? (
                <img
                  src={currentBadgeRange.icon.url}
                  alt={currentBadge}
                  className="w-full h-full object-cover"
                />
              ) : (
                <Star size={24} className={isDark ? 'text-orange-400' : 'text-orange-500'} fill={isDark ? 'rgba(251, 146, 60, 0.5)' : 'currentColor'} />
              )}
            </div>
            <div className="flex flex-col">
              <h2 className={`text-lg font-black ${isDark ? 'text-white' : 'text-slate-900'} mb-1`}>
                {currentBadge} Badge
              </h2>
              <span className={`text-[10px] font-black uppercase tracking-widest ${isDark ? 'text-white/60' : 'text-slate-400'
                }`}>
                CURRENT LEVEL
              </span>
            </div>
          </div>
          <Button
            type="text"
            icon={<X size={16} />}
            onClick={onClose}
            className={`${isDark ? 'text-white/60 hover:text-white hover:bg-white/10' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'} !rounded-full`}
          />
        </div>

        {/* Description */}
        <div className="px-6 py-5">
          <p className={`text-sm leading-relaxed ${isDark ? 'text-white/80' : 'text-slate-700'}`}>
            {currentDescription}
          </p>
        </div>

        {/* Next Badge Section - Only show if there's a next badge and current badge is not the last badge */}
        {nextBadge && nextBadge !== currentBadge && !isLastBadge() && (
          <div className={`px-6 pb-6 ${isDark ? 'border-t border-white/10 pt-5' : 'border-t border-slate-100 pt-5'}`}>
            <span className={`text-[10px] font-black uppercase tracking-widest block mb-3 ${isDark ? 'text-white/60' : 'text-slate-400'
              }`}>
              NEXT BADGE
            </span>
            <div className={`p-4 rounded-xl border ${isDark
                ? 'bg-white/5 border-white/10'
                : 'bg-slate-50 border-slate-100'
              }`}>
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center overflow-hidden ${isDark ? 'bg-blue-500/10 border border-blue-500/20' : 'bg-blue-50 border border-blue-100'}`}>
                  {nextBadgeRange?.icon?.url ? (
                    <img
                      src={nextBadgeRange.icon.url}
                      alt={nextBadge}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Trophy size={24} className={isDark ? 'text-blue-400' : 'text-blue-600'} fill={isDark ? 'rgba(59, 130, 246, 0.5)' : 'currentColor'} />
                  )}
                </div>
                <div className="flex flex-col flex-1">
                  <h3 className={`text-base font-black mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    {nextBadge}
                  </h3>
                  <p className={`text-sm ${isDark ? 'text-white/80' : 'text-slate-700'}`}>
                    {nextCriteria}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default BadgeInfoModal;
